"use client";

import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import { getProfileById, updateProfileRelevantLocation, getProfileAppearance, updateProfileAppearance } from '@/lib/supabase/profiles';
import { upsertUserAddress } from '@/lib/supabase/userAddresses';
import { migrateAnonymousChatOnLogin } from '@/lib/supabase/chat';
import { clearAnonymousSupportStoreData, listAnonymousSupportSessionEntries } from '@/lib/anonymousSupportChat';
import { preloadTitilliumFont } from '@/lib/fontGovernance';
import { DEFAULT_LOCATIONS } from '@/lib/defaultLocations';
import { clearRememberSessionWindow, isRememberSessionExpired } from '@/lib/authPersistence';

interface UserProfile {
  id: string;
  username: string;
  avatar_url: string | null;
  name?: string | null;
  surname?: string | null;
  transport_type?: string | null;
  relevant_location_display_name: string | null;
  relevant_latitude: number | null;
  relevant_longitude: number | null;
  email?: string | null;
  bank_accounts?: any[];
  bank_cbu?: string | null;
  bank_name?: string | null;
  bank_account_holder?: string | null;
  phone_number?: string | null;
  appearance_settings?: {
    palette?: string;
    customPrimary?: string;
    customAccent?: string;
    customForeground?: string;
    theme?: 'light' | 'dark' | 'auto';
    darkNotch?: boolean;
  };
  verification_photos?: {
    dniAnh: string | null;
    dniRev: string | null;
    withDni: string | null;
  };
  verification_status?: 'pending' | 'submitted' | 'verified' | 'rejected' | 'expired';
  verified_at?: string | null;
}

interface SessionContextType {
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  relevantLocation: { displayName: string; latitude: number; longitude: number } | null;
  setRelevantLocation: (location: { displayName: string; latitude: number; longitude: number } | null) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { toast } = useToast();
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoadingState] = useState(true);
  const setLoading = useCallback((val: boolean) => {
    setLoadingState(prev => prev === val ? prev : val);
  }, []);
  const defaultCityLocation = {
    displayName: DEFAULT_LOCATIONS.SALTA.displayName,
    latitude: DEFAULT_LOCATIONS.SALTA.latitude,
    longitude: DEFAULT_LOCATIONS.SALTA.longitude,
  };
  const [relevantLocation, setRelevantLocationState] = useState<{ displayName: string; latitude: number; longitude: number } | null>(defaultCityLocation);
  const migratedUsersRef = React.useRef<Set<string>>(new Set());

  const isInvalidRefreshTokenError = useCallback((error: unknown) => {
    const message = typeof error === 'string'
      ? error
      : (error as { message?: string } | null | undefined)?.message || '';
    const lower = message.toLowerCase();
    return lower.includes('invalid refresh token') || lower.includes('refresh token not found');
  }, []);

  const clearSupabaseAuthStorage = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (key.startsWith('sb-') && key.includes('auth-token')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (e) {
      console.warn('[SessionContext] Could not clear Supabase auth storage:', e);
    }
  }, []);

  const recoverFromInvalidRefreshToken = useCallback(async (source: string, error?: unknown) => {
    console.warn(`[SessionContext] ${source}: invalid refresh token detected. Clearing stale auth session.`, error);
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      console.warn('[SessionContext] Local signOut after invalid refresh token failed:', e);
    }
    clearRememberSessionWindow();
    clearSupabaseAuthStorage();
    setSession(null);
    setProfile(null);
    setLoading(false);
  }, [clearSupabaseAuthStorage, setLoading]);

  const setRelevantLocation = useCallback((location: { displayName: string; latitude: number; longitude: number } | null) => {
    
    setRelevantLocationState(current => {
      if (!current && !location) return null;
      if (current && location && 
          current.displayName === location.displayName && 
          current.latitude === location.latitude && 
          current.longitude === location.longitude) {
        return current;
      }
      return location;
    });
    
    // Si el usuario no está logueado, guardar en localStorage
    if (!session && location) {
      try {
        localStorage.setItem('relevant_location', JSON.stringify(location));
      } catch (e) {
        console.warn('[SessionContext] Could not save location to localStorage:', e);
      }
    }
  }, [session]);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const userProfile = await getProfileById(userId);
      setProfile(prev => {
        if (!prev && !userProfile) return null;
        if (prev && userProfile && 
            prev.id === userProfile.id && 
            prev.username === userProfile.username && 
            prev.avatar_url === userProfile.avatar_url &&
          prev.name === userProfile.name &&
          prev.surname === userProfile.surname &&
            prev.relevant_location_display_name === userProfile.relevant_location_display_name &&
            prev.relevant_latitude === userProfile.relevant_latitude &&
            prev.relevant_longitude === userProfile.relevant_longitude) {
          return prev;
        }
        return userProfile;
      });
    } catch (error) {
      console.error("[fetchProfile] ERROR fetching profile:", error);
      setProfile(null);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (session?.user?.id) {
      await fetchProfile(session.user.id);
    } else {
    }
  }, [session, fetchProfile]);

  const migrateAllAnonymousSupportChats = useCallback(async (userId: string) => {
    const entries = listAnonymousSupportSessionEntries();
    if (!entries.length) {
      return;
    }

    let migratedCount = 0;

    for (const entry of entries) {
      try {
        // Migrate the group chat associated with this anonymous session
        // The RPC will add the user to the group chat and link their messages
        await migrateAnonymousChatOnLogin(userId, entry.sessionId);
        migratedCount++;
      } catch (error) {
        console.error('[SessionContext] Error migrating anonymous group chat:', {
          storeId: entry.storeId,
          sessionId: entry.sessionId,
          error,
        });
      } finally {
        clearAnonymousSupportStoreData(entry.storeId);
      }
    }

    if (migratedCount > 0) {
      window.dispatchEvent(new CustomEvent('anonymous-chat-migrated', {
        detail: { migratedCount }
      }));
    }
  }, []);

  // Función para migrar datos pendientes de localStorage a Supabase
  const migratePendingData = useCallback(async (userId: string) => {
    // Evitar múltiples migraciones para el mismo usuario
    if (migratedUsersRef.current.has(userId)) {
      return;
    }
    
    let hasMigratedSomething = false;
    
    try {
      // Migrar direcciones pendientes
      const pendingAddressesStr = localStorage.getItem('pending_addresses');
      if (pendingAddressesStr) {
        const pendingAddresses = JSON.parse(pendingAddressesStr);
        
        if (pendingAddresses.length > 0) {
          for (const addr of pendingAddresses) {
            try {
              await upsertUserAddress({
                user_id: userId,
                label: addr.label || addr.location.displayName,
                is_primary: addr.is_primary || false,
                location: addr.location,
                floor: addr.floor || null,
                apartment: addr.apartment || null,
                notes: addr.notes || null,
              });
              hasMigratedSomething = true;
            } catch (e) {
              console.error('[migratePendingData] Error migrating address:', e);
            }
          }
          
          localStorage.removeItem('pending_addresses');
        }
      }
      
      // Migrar relevant_location si existe y el perfil no tiene una
      const relevantLocationStr = localStorage.getItem('relevant_location');
      if (relevantLocationStr) {
        const relevantLoc = JSON.parse(relevantLocationStr);
        
        // Verificar si el perfil ya tiene relevant_location
        const userProfile = await getProfileById(userId);
        if (userProfile && !userProfile.relevant_location_display_name) {

          await updateProfileRelevantLocation(
            userId,
            relevantLoc.displayName,
            relevantLoc.latitude,
            relevantLoc.longitude
          );
          
          // Actualizar el estado local
          setRelevantLocation(relevantLoc);
          hasMigratedSomething = true;
        } else if (!userProfile) {
          console.warn('[migratePendingData] Profile not found for userId during migration:', userId);
        }
        
        // Limpiar localStorage solo después de migrar
        localStorage.removeItem('relevant_location');
      }

      // Migrar preferencias de apariencia guardadas como guest
      const guestAppearanceStr = localStorage.getItem('guest-appearance-settings');
      if (guestAppearanceStr) {
        try {
          const guestAppearance = JSON.parse(guestAppearanceStr);

          // Obtener las preferencias actuales del usuario
          const existingAppearance = await getProfileAppearance(userId);
          
          // Decidir qué preferencias usar:
          // - Si el usuario no tiene preferencias, usar las del guest
          // - Si el usuario tiene preferencias, mezclar (guest guest tiene prioridad en campos específicos)
          let mergedAppearance;
          if (!existingAppearance || Object.keys(existingAppearance).length === 0) {
            // Usuario sin preferencias previas: usar las del guest
            mergedAppearance = guestAppearance;
          } else {
            // Usuario con preferencias: mezclar manteniendo las existentes pero actualizando con guest
            mergedAppearance = {
              ...existingAppearance,
              ...guestAppearance,
            };
          }
          
          // Guardar las preferencias migradas
          await updateProfileAppearance(userId, mergedAppearance);
          
          // Limpiar localStorage después de migrar
          localStorage.removeItem('guest-appearance-settings');
          
          hasMigratedSomething = true;
        } catch (e) {
          console.error('[migratePendingData] Error migrating appearance settings:', e);
        }
      }
      
      // Marcar usuario como migrado
      migratedUsersRef.current.add(userId);
      
      // Solo mostrar toast si realmente se migró algo
      if (hasMigratedSomething) {
        toast({
          title: 'Datos guardados',
          description: 'Tu dirección, ubicación y preferencias fueron guardadas en tu cuenta.',
        });
      }
    } catch (error) {
      console.error('[migratePendingData] Error during migration:', error);
    }
  }, [toast, setRelevantLocationState]);

  useEffect(() => {
    setLoading(true);
    // Fallback: if auth state doesn't resolve within 5s, stop showing the global loading skeleton
    // Helper: try to parse session from URL and also ask client for any stored session
    const attemptRestoreSession = async (retries = 2) => {
      try {
        if (typeof window === 'undefined') return null;
        if (typeof supabase.auth.getSessionFromUrl === 'function') {
          try {
            const { data, error } = await supabase.auth.getSessionFromUrl();
            if (error) console.warn('[SessionContext] getSessionFromUrl error:', error.message || error);
            if (data?.session) {
              setSession(data.session as any);
              return data.session;
            }
          } catch (e) {
            console.warn('[SessionContext] getSessionFromUrl threw:', e);
          }
        }

        // Fallback: try to read any stored session from the client
        try {
          const s = await supabase.auth.getSession();
          const authError = (s as any)?.error;
          if (authError) {
            if (isInvalidRefreshTokenError(authError)) {
              await recoverFromInvalidRefreshToken('attemptRestoreSession.getSession', authError);
              return null;
            }
            console.warn('[SessionContext] getSession error:', authError?.message || authError);
          }
          // supabase.auth.getSession() returns { data: { session } }
          // guard for different shapes
          const sess = (s as any)?.data?.session ?? (s as any)?.session ?? null;
          if (sess) {
            setSession(sess as any);
            return sess;
          }
        } catch (e) {
          console.warn('[SessionContext] getSession() threw:', e);
        }
      } catch (e) {
        console.warn('[SessionContext] attemptRestoreSession error:', e);
      }

      if (retries > 0) {
        // small backoff and retry
        await new Promise((r) => setTimeout(r, 1500));
        return attemptRestoreSession(retries - 1);
      }
      return null;
    };

    const fallback = setTimeout(async () => {
      console.warn('[SessionContext] Fallback: auth state did not resolve within timeout. Trying to restore session once more before giving up.');
      try {
        const restored = await attemptRestoreSession(1);
        if (restored) {
          setLoading(false);
          return;
        }
      } catch (e) {
        console.warn('[SessionContext] Fallback restore attempt threw:', e);
      }
      console.warn('[SessionContext] Fallback: no session restored. Setting loading to FALSE to avoid stuck UI.');
      setLoading(false);
    }, 5000);
    // Try to extract a session from the redirect URL (magic link / OAuth)
    (async () => {
      try {
        // supabase.auth.getSessionFromUrl will parse the URL and persist the session if present
        if (typeof window !== 'undefined' && typeof supabase.auth.getSessionFromUrl === 'function') {
          try {
            const { data, error } = await supabase.auth.getSessionFromUrl();
            if (error) console.warn('[SessionContext] getSessionFromUrl error:', error.message || error);
            if (data?.session) {
              setSession(data.session as any);
              try { clearTimeout(fallback); } catch (e) {}
              return;
            }
          } catch (e) {
            console.warn('[SessionContext] getSessionFromUrl threw:', e);
          }
        }

        // Immediate attempt: try to read any stored session from the client right away
        try {
          const s = await supabase.auth.getSession();
          const authError = (s as any)?.error;
          if (authError) {
            if (isInvalidRefreshTokenError(authError)) {
              await recoverFromInvalidRefreshToken('bootstrap.getSession', authError);
              try { clearTimeout(fallback); } catch (e) {}
              return;
            }
            console.warn('[SessionContext] immediate getSession error:', authError?.message || authError);
          }
          const sess = (s as any)?.data?.session ?? (s as any)?.session ?? null;
          if (sess) {
            setSession(sess as any);
            try { clearTimeout(fallback); } catch (e) {}
            return;
          }
        } catch (e) {
          console.warn('[SessionContext] immediate getSession() threw:', e);
        }
      } catch (e) {}
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {

      if (session && isRememberSessionExpired()) {
        console.warn('[SessionContext] remember-me window expired. Signing out user.');
        clearRememberSessionWindow();
        try {
          await supabase.auth.signOut({ scope: 'global' });
        } catch (e) {
          console.error('[SessionContext] Error signing out expired session:', e);
        }
        setSession(null);
        setProfile(null);
        setLoading(false);
        toast({
          title: 'Sesión vencida',
          description: 'Tu inicio de sesión de 30 días venció. Inicia sesión nuevamente.',
        });
        return;
      }

      setSession(prev => {
        if (!prev && !session) return null;
        if (prev?.access_token === session?.access_token && prev?.user?.id === session?.user?.id) {
          return prev;
        }
        return session;
      });
      if (session) {
        try { clearTimeout(fallback); } catch (e) {}
        
        // Si es un nuevo login (SIGNED_IN), migrar datos pendientes
        if (_event === 'SIGNED_IN' && session.user?.id) {
          // No bloqueamos el flujo principal con la migración
          migratePendingData(session.user.id).catch(e => {
            console.error('[SessionContext] background migration failed:', e);
          });
          
          // Auto cart migration disabled - user triggers it manually via CheckoutSidebar
          // try {
          //   const { getTempCartAsItems, clearTempCart } = await import('@/lib/tempCart');
          //   const { migrateTempCart } = await import('@/lib/supabase/carts');
          //   const tempItems = getTempCartAsItems();
          //   if (tempItems.length > 0) {
          //     const STORE_ID = '30e79df7-81e8-4c5f-a825-602e5899d961';
          //     await migrateTempCart(session.user.id, tempItems, STORE_ID);
          //     clearTempCart();
          //     window.dispatchEvent(new CustomEvent('cart-updated'));
          //   }
          // } catch (e) {
          //   console.error('[SessionContext] Error migrating temp cart:', e);
          // }

          // Migrar chats anónimos por tienda
          migrateAllAnonymousSupportChats(session.user.id).catch((e) => {
            console.error('[SessionContext] Error migrating anonymous chats:', e);
          });

          // Ensure new users get a default relevant city (Salta) if their profile lacks it.
          try {
            const existingProfile = await getProfileById(session.user.id);
            if (existingProfile && !existingProfile.relevant_location_display_name) {
              const defaultCityLocation = {
                displayName: DEFAULT_LOCATIONS.SALTA.displayName,
                latitude: DEFAULT_LOCATIONS.SALTA.latitude,
                longitude: DEFAULT_LOCATIONS.SALTA.longitude,
              };
              try {
                await updateProfileRelevantLocation(
                  session.user.id,
                  defaultCityLocation.displayName,
                  defaultCityLocation.latitude,
                  defaultCityLocation.longitude
                );
                // Update local state so UI reflects the change immediately
                setRelevantLocationState(defaultCityLocation);
              } catch (e) {
                console.warn('[SessionContext] Could not set default relevant location on SIGNED_IN:', e);
              }
            }
          } catch (e) {
            console.warn('[SessionContext] Error ensuring default city on SIGNED_IN:', e);
          }
        }

        if (_event !== 'SIGNED_OUT' && _event !== 'SIGNED_IN' && session.user?.id) {
          migrateAllAnonymousSupportChats(session.user.id).catch((e) => {
            console.error('[SessionContext] Background anonymous chat migration failed:', e);
          });
        }
      }
      // If the session is removed, we can stop loading immediately.
      if (!session) {
        setProfile(null);
        // Set default location to Salta for unauthenticated users
        setRelevantLocationState({
          displayName: DEFAULT_LOCATIONS.SALTA.displayName,
          latitude: DEFAULT_LOCATIONS.SALTA.latitude,
          longitude: DEFAULT_LOCATIONS.SALTA.longitude,
        });
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(fallback);
      subscription.unsubscribe();
    };
  }, [
    migrateAllAnonymousSupportChats,
    migratePendingData,
    isInvalidRefreshTokenError,
    recoverFromInvalidRefreshToken,
    setLoading,
  ]); // Empty dependency array intent preserved; deps are stable callbacks.

  useEffect(() => {
    if (session?.user) {
      if (isRememberSessionExpired()) {
        console.warn('[SessionContext] Session found but remember-me window already expired. Signing out now.');
        clearRememberSessionWindow();
        supabase.auth.signOut({ scope: 'global' }).catch((e) => {
          console.error('[SessionContext] Error signing out expired session (session effect):', e);
        });
        setSession(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      
      // Use a timeout to ensure we don't hang forever if fetchProfile is slow/stuck
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout')), 4000)
      );

      Promise.race([
        fetchProfile(session.user.id),
        timeoutPromise
      ]).catch(err => {
        console.warn("[SessionContext] Profile fetch error or timeout:", err);
        // We continue anyway, loading should be set to false
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [session, fetchProfile]);

  // When profile becomes available, preload the canonical font
  useEffect(() => {
    if (!profile?.id) return;
    try {
      preloadTitilliumFont();
    } catch (e) {
      console.warn('[SessionContext] could not preload font:', e);
    }
  }, [profile?.id]);


  // Default to Salta for the navbar selector unless the user explicitly chose another city.
  useEffect(() => {
    const detectLocation = async () => {
      try {
        const defaultCityLocation = {
          displayName: DEFAULT_LOCATIONS.SALTA.displayName,
          latitude: DEFAULT_LOCATIONS.SALTA.latitude,
          longitude: DEFAULT_LOCATIONS.SALTA.longitude,
        };

        const clearStaleLocalLocation = () => {
          try {
            localStorage.removeItem('relevant_location');
            localStorage.removeItem('city_selector_override');
          } catch (e) {
            console.warn('[SessionContext detectLocation] Could not clear stale local location:', e);
          }
        };

        try {
          const hasCityOverride = localStorage.getItem('city_selector_override') === '1';
          if (hasCityOverride) {
            const stored = localStorage.getItem('relevant_location');
            if (stored) {
              const parsed = JSON.parse(stored);
              if (parsed?.displayName && parsed?.latitude != null && parsed?.longitude != null) {
                setRelevantLocation(parsed);
                return;
              }
            }

            clearStaleLocalLocation();
          } else {
            // If the guest has no explicit city override, ignore any stale stored location.
            clearStaleLocalLocation();
          }
        } catch (e) {
          console.warn('[SessionContext detectLocation] Could not read city selector override:', e);
        }

        // Siempre dejar Salta como fallback inmediato para no logueados
        setRelevantLocation(defaultCityLocation);

        try {
          localStorage.setItem('relevant_location', JSON.stringify(defaultCityLocation));
        } catch (e) {
          console.warn('[SessionContext] Could not save default location to localStorage:', e);
        }

        if (session?.user?.id) {
          try {
            await updateProfileRelevantLocation(
              session.user.id,
              defaultCityLocation.displayName,
              defaultCityLocation.latitude,
              defaultCityLocation.longitude
            );
          } catch (e) {
            console.warn('[SessionContext detectLocation] Could not save default location to profile:', e);
          }
        }
      } catch (e) {
        console.error('[SessionContext detectLocation] Unexpected error:', e);
        const defaultCityFallback = {
          displayName: DEFAULT_LOCATIONS.SALTA.displayName,
          latitude: DEFAULT_LOCATIONS.SALTA.latitude,
          longitude: DEFAULT_LOCATIONS.SALTA.longitude,
        };
        setRelevantLocation(defaultCityFallback);
      }
    };
    if (!loading) {
      detectLocation();
    }
  }, [session?.user?.id, loading, setRelevantLocation]);

  // Listen for address changes to re-sync location
  useEffect(() => {
    const handleAddressChange = () => {
      // Keep city selector as source of truth for relevant location.
      // Address updates should not reset city override.
      if (!loading && session && profile) {
        // Trigger location detection again
        setTimeout(() => {
          // Force a re-detection by re-fetching profile
          if (session?.user?.id) {
            fetchProfile(session.user.id);
          }
        }, 100);
      }
    };

    window.addEventListener('address-updated', handleAddressChange);
    return () => window.removeEventListener('address-updated', handleAddressChange);
  }, [loading, session, profile, fetchProfile]);


  const contextValue = React.useMemo(() => ({
    session,
    profile,
    loading,
    refreshProfile,
    relevantLocation,
    setRelevantLocation
  }), [session, profile, loading, refreshProfile, relevantLocation, setRelevantLocation]);

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};